import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useWebRTC } from '../context/WebRTCContext';
import { getLastSeen, markSeen, subscribeSeen } from './useSeenCutoff';

/**
 * Maps Sacred Timeline event type prefixes to sidebar nav item IDs.
 * When an event fires, the corresponding nav item gets a badge.
 */
const EVENT_TO_NAV_MAP = {
    // Activity events → Timeline
    'activity.': 'sacred-timeline',

    // Chat events → Chat
    'chat.': 'chat',

    // Interrogation events → Interrogation
    'interrogation.': 'interrogation',

    // Economy events → Economy
    'economy.': 'economy',

    // Challenge events → Challenges
    'challenge.': 'challenges',

    // Spin wheel events → Spin Wheel (real emitted prefix is `spin_wheel.`)
    'spin_wheel.': 'spin-wheel',

    // PiShock — a delivered command badges the PiShock page
    'pishock.': 'pishock',

    // Write Lines (real emitted prefix is `write_lines.`)
    'write_lines.': 'write-lines',

    // Limit events → Limitations (covers lockouts + app/file blocks)
    'limit.': 'time-limit',

    // Dossier analysis → Dossier (real emitted events; the old `ai.*` keys were dead)
    'dossier.analysis_completed': 'dossier',
    'dossier.question_added': 'dossier',

    // Journal events → Journal (a new entry should badge the Journal nav item)
    'journal.': 'journal',
};

/**
 * Event types that are "high priority" — shown with a pulsing dot
 */
const HIGH_PRIORITY_EVENTS = new Set([
    'activity.keyword_flagged',
    'pishock.command_completed',
    'limit.app_blocked',
    'limit.file_blocked',
    'dossier.analysis_completed',
]);

/**
 * Route prefix → nav item ID mapping for clearing badges on visit
 */
const ROUTE_TO_NAV_MAP = {
    '/app/monitoring/sacred-timeline': 'sacred-timeline',
    '/app/monitoring/file-browser': 'file-browser',
    '/app/monitoring/keywords': 'keywords',
    '/app/monitoring/dossier': 'dossier',
    '/app/monitoring/time-limit': 'time-limit',
    '/app/communication/chat': 'chat',
    '/app/communication/interrogation': 'interrogation',
    '/app/communication/journal': 'journal',
    '/app/punishment/spin-wheel': 'spin-wheel',
    '/app/punishment/economy': 'economy',
    '/app/punishment/write-lines': 'write-lines',
    '/app/punishment/input-sabotage': 'input-sabotage',
    '/app/games/challenges': 'challenges',
    '/app/integrations/pishock': 'pishock',
    '/app/integrations/gaze-tracking': 'gaze-tracking',
    '/app/integrations/cameras': 'camera-monitor',
    '/app/integrations/anthropic': 'anthropic',
    '/app/games/corruption': 'corruption',
    '/app/games/siege': 'siege',
    '/app/games/task-board': 'task-board',
};

/**
 * Resolves which nav item ID an event type maps to
 */
function resolveNavId(eventType) {
    // Check exact match first
    if (EVENT_TO_NAV_MAP[eventType]) return EVENT_TO_NAV_MAP[eventType];

    // Check prefix match
    for (const [prefix, navId] of Object.entries(EVENT_TO_NAV_MAP)) {
        if (prefix.endsWith('.') && eventType.startsWith(prefix)) {
            return navId;
        }
    }
    return null;
}

/**
 * Nav items whose badge must agree EXACTLY with an on-page "N new since your
 * last visit" indicator (see useSeenCutoff / NewSinceIndicator). For these:
 *   - events are keyed by their item timestamp (an analysis `created_at`), so
 *     the badge and the page count the same underlying rows against the same
 *     shared cutoff — and a live event that duplicates a seeded row collapses
 *     onto the same key instead of double-counting;
 *   - arriving on the page does NOT advance the cutoff (the page owns that, on
 *     leave, via useSeenCutoff) so this-visit items stay highlighted;
 *   - the badge is seeded from the DB on connect (see the seed effect) so the
 *     count survives a refresh instead of resetting to zero.
 * Every other nav keeps the simple "clear on arrival" behaviour.
 */
const CUTOFF_MANAGED_NAVS = new Set(['dossier']);

/** Canonical timestamp for an event — dossier analyses carry `created_at`. */
function eventStamp(event) {
    const data = event.data || event.details || {};
    return data.created_at || event.timestamp || event.ts || Date.now();
}

/**
 * Hook that tracks unread activity badges for sidebar nav items.
 *
 * Returns a Map<navItemId, { count: number, priority: 'high' | 'normal' }>
 * Badges auto-clear when the user navigates to the corresponding view.
 *
 * @returns {{ badges: Object, clearBadge: (navId: string) => void }}
 */
export function useSidebarBadges() {
    const { featureManager, isConnected } = useWebRTC();
    const location = useLocation();
    const [badges, setBadges] = useState({});
    const currentNavRef = useRef(null);
    // navId -> { items: Map<key, ts>, priority }. The single in-memory ledger of
    // unseen activity; the visible `badges` are derived from it by counting only
    // items newer than each nav's shared last-seen cutoff.
    const storeRef = useRef(new Map());
    const seqRef = useRef(0);   // makes keyless (non-cutoff) events unique
    const seededRef = useRef(false);

    // Rebuild `badges` from the ledger, pruning anything now older than its
    // cutoff so the count always equals "items new since your last visit" —
    // the exact quantity the page's NewSinceIndicator shows.
    const recompute = useCallback(() => {
        const next = {};
        for (const [navId, entry] of storeRef.current) {
            const cutoff = getLastSeen(navId);
            for (const [key, ts] of entry.items) {
                if (ts <= cutoff) entry.items.delete(key);
            }
            if (entry.items.size === 0) { storeRef.current.delete(navId); continue; }
            next[navId] = { count: entry.items.size, priority: entry.priority };
        }
        setBadges(next);
    }, []);

    // Fold one event into the ledger.
    const record = useCallback((navId, event) => {
        let entry = storeRef.current.get(navId);
        if (!entry) { entry = { items: new Map(), priority: 'normal' }; storeRef.current.set(navId, entry); }
        const ts = eventStamp(event);
        // Cutoff-managed navs key by timestamp so a live event and a seeded row
        // for the same item collapse; everything else keys by a running seq so
        // each event counts once even when timestamps collide.
        const key = CUTOFF_MANAGED_NAVS.has(navId) ? `t${ts}` : `s${++seqRef.current}`;
        entry.items.set(key, ts);
        if (HIGH_PRIORITY_EVENTS.has(event.type)) entry.priority = 'high';
        recompute();
    }, [recompute]);

    // Track the active route; on arrival, non-cutoff pages are marked seen (the
    // classic "clear on visit"). Cutoff-managed pages are left alone — their own
    // useSeenCutoff advances the cutoff when you LEAVE, which clears the badge in
    // lockstep with the page indicator (via the subscribeSeen effect below).
    useEffect(() => {
        const navId = ROUTE_TO_NAV_MAP[location.pathname] || null;
        currentNavRef.current = navId;
        if (navId && !CUTOFF_MANAGED_NAVS.has(navId)) {
            storeRef.current.delete(navId);
            markSeen(navId, Date.now());
        }
        recompute();
    }, [location.pathname, recompute]);

    // Recompute whenever ANY page advances its cutoff (e.g. leaving Dossier).
    useEffect(() => subscribeSeen(() => recompute()), [recompute]);

    // Clear a specific badge manually
    const clearBadge = useCallback((navId) => {
        storeRef.current.delete(navId);
        markSeen(navId, Date.now());
        recompute();
    }, [recompute]);

    // Subscribe to Sacred Timeline events
    useEffect(() => {
        if (!featureManager || !isConnected) return;

        const sacredFeature = featureManager.getFeature('sacred-timeline');
        if (!sacredFeature) return;

        const unsubLive = sacredFeature.on('sacred_event', (eventData) => {
            const event = eventData.payload || eventData;
            // Skip ephemeral events (high-frequency telemetry, state syncs)
            if (!event || !event.type || event.persistence === 'ephemeral') return;
            const navId = resolveNavId(event.type);
            if (!navId) return;
            // Don't badge the currently active page
            if (navId === currentNavRef.current) return;
            record(navId, event);
        });

        return () => unsubLive();
    }, [featureManager, isConnected, record]);

    // Cold-start seed: after a refresh the live-event ledger is empty, so the
    // Dossier badge would read 0 while the page (reading the DB) shows N new.
    // Seed the badge from the same DB rows, keyed by created_at, so the two
    // agree immediately — and a later live event for a seeded row dedupes.
    useEffect(() => {
        if (seededRef.current || !featureManager || !isConnected) return;
        const dossier = featureManager.getFeature('dossier');
        if (!dossier || typeof dossier.getAnalyses !== 'function') return;
        seededRef.current = true;
        let cancelled = false;
        Promise.resolve(dossier.getAnalyses(10)).then((res) => {
            if (cancelled || currentNavRef.current === 'dossier') return;
            if (!res?.success || !Array.isArray(res.analyses) || res.analyses.length === 0) return;
            let entry = storeRef.current.get('dossier');
            if (!entry) { entry = { items: new Map(), priority: 'high' }; storeRef.current.set('dossier', entry); }
            for (const a of res.analyses) {
                const ts = a?.created_at;
                if (typeof ts === 'number') entry.items.set(`t${ts}`, ts);
            }
            recompute();
        }).catch(() => { /* seed is best-effort; live events still populate the badge */ });
        return () => { cancelled = true; };
    }, [featureManager, isConnected, recompute]);

    return { badges, clearBadge };
}
